import { useEffect, useState, useRef } from "react";
import { UNSAFE_NavigationContext as NavigationContext, useBlocker } from "react-router-dom";
import { useContext } from "react";

// Definieren von Typen für den History-basierten Navigator
interface Navigator {
    block: (blocker: (tx: Transaction) => boolean | void) => () => void;
    navigate: (to: string, options?: any) => void;
}

interface Transaction {
    location: {
        pathname: string;
        [key: string]: any;
    };
    [key: string]: any;
}

/**
 * Ein benutzerdefinierter Hook, der eine Navigation blockiert, wenn es ungespeicherte Änderungen gibt.
 * Dieser Hook versucht zuerst, den eingebauten useBlocker-Hook zu verwenden, und fällt bei Bedarf auf
 * eine Legacy-Implementierung zurück.
 * 
 * @param hasUnsavedChanges - Boolean, der angibt, ob es ungespeicherte Änderungen gibt
 * @returns Ein Objekt mit Zustand und Methoden zur Steuerung des Blockierverhaltens
 */
export function useUnsavedChangesBlocker(hasUnsavedChanges: boolean) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [blockedLocation, setBlockedLocation] = useState<string | null>(null);
    const [shouldUseLegacy, setShouldUseLegacy] = useState(false);
    const blockerRef = useRef<any>(null);
    
    // Immer useBlocker aufrufen, um die React-Hook-Regeln zu erfüllen
    // Wir werden das Ergebnis aber nur verwenden, wenn wir keinen Legacy-Modus benötigen
    const builtInBlocker = useBlocker(
        ({ currentLocation, nextLocation }) => 
            hasUnsavedChanges && 
            currentLocation.pathname !== nextLocation.pathname && 
            !shouldUseLegacy
    );
    
    // Speichere den aktuellen Blocker in der Ref
    blockerRef.current = builtInBlocker;

    // Beim ersten Rendern prüfen, ob useBlocker korrekt funktioniert
    useEffect(() => {
        try {
            // Eine einfache Eigenschaft des Blockers abfragen, um zu sehen, ob er funktioniert
            const _ = builtInBlocker.state;
        } catch (error) {
            // Falls useBlocker einen Fehler wirft, verwende den Legacy-Ansatz
            if (!shouldUseLegacy) {
                console.warn("useBlocker wird nicht unterstützt, falle auf Legacy-Implementierung zurück");
                setShouldUseLegacy(true);
            }
        }
    }, [builtInBlocker, shouldUseLegacy]);

    // Zugriff auf den NavigationContext für Legacy-Implementierung
    const navigationContext = useContext(NavigationContext);
    const navigator = navigationContext?.navigator as unknown as Navigator;

    // Legacy-Implementierung für die Navigation-Blockierung
    useEffect(() => {
        if (!shouldUseLegacy || !hasUnsavedChanges || !navigator?.block) return;

        try {
            // Navigation blockieren und eine Callback-Funktion registrieren
            const unblock = navigator.block((tx: Transaction) => {
                // Speichere den Zielort für die spätere Navigation
                setBlockedLocation(tx.location.pathname);
                // Aktiviere den Dialog
                setShowPrompt(true);
                // Blockiere die Navigation
                return false;
            });

            // Event-Listener aufräumen, wenn die Komponente unmountet oder sich die Abhängigkeiten ändern
            return unblock;
        } catch (error) {
            console.error("Navigation blocking failed:", error);
            return undefined;
        }
    }, [hasUnsavedChanges, navigator, shouldUseLegacy]);

    // Funktion zum Fortsetzen der Navigation
    const proceed = () => {
        if (!shouldUseLegacy && blockerRef.current) {
            blockerRef.current.proceed?.();
            return;
        }

        setShowPrompt(false);
        
        // Legacy: Wenn ein blockierter Standort gespeichert ist, navigiere dorthin
        if (blockedLocation && navigator?.block && navigator?.navigate) {
            try {
                // Temporär die Blockierung aufheben
                const unblock = navigator.block(() => true);
                // Zur blockierten Route navigieren
                navigator.navigate(blockedLocation);
                // Blockierung wieder aktivieren
                unblock();
            } catch (error) {
                console.error("Navigation failed:", error);
            }
            // Zurücksetzen des blockierten Standorts
            setBlockedLocation(null);
        }
    };

    // Funktion zum Abbrechen der Navigation
    const reset = () => {
        if (!shouldUseLegacy && blockerRef.current) {
            blockerRef.current.reset?.();
            return;
        }

        setShowPrompt(false);
        setBlockedLocation(null);
    };

    // Füge Browser-Warnung bei Verlassen/Neuladen der Seite hinzu
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                // Standard-Nachricht für Browser-Dialog
                e.preventDefault();
                e.returnValue = ''; // Für Chrome erforderlich
                return true;
            }
            return undefined;
        };

        // Registriere Event-Listener nur wenn es ungespeicherte Änderungen gibt
        if (hasUnsavedChanges) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        // Cleanup beim Unmount oder wenn hasUnsavedChanges sich ändert
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    // Wenn der integrierte Blocker verfügbar ist und verwendet werden soll
    if (!shouldUseLegacy && blockerRef.current) {
        return {
            showPrompt: blockerRef.current.state === "blocked",
            proceed,
            reset,
            state: blockerRef.current.state
        };
    }

    // Andernfalls verwende den Zustand aus der Legacy-Implementierung
    return {
        showPrompt,
        proceed,
        reset,
        state: showPrompt ? "blocked" : "unblocked",
    };
} 